import { io, Socket } from 'socket.io-client';
import { getWebSocketUrl, logWebSocketConfig } from '../utils/websocketResolver';

interface WebSocketEventCallbacks {
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
  onNewDeal?: (deal: any) => void;
  onDealUpdated?: (update: any) => void;
  onNewComment?: (comment: any) => void;
  onNotification?: (notification: any) => void;
  onError?: (error: any) => void;
  [key: string]: ((data?: any) => void) | undefined;
}

class WebSocketService {
  private internalSocket: Socket | null = null;
  private internalCallbacks: WebSocketEventCallbacks = {};
  private internalIsConnecting = false;
  private internalReconnectAttempts = 0;
  private internalMaxReconnectAttempts = 5;
  private internalReconnectDelay = 1000; // Start with 1 second

  /**
   * Initialize WebSocket connection
   */
  connect(url?: string): void;
  connect(callbacks: WebSocketEventCallbacks): Promise<boolean>;
  connect(urlOrCallbacks?: string | WebSocketEventCallbacks): void | Promise<boolean> {
    // Handle overloaded method signatures
    if (typeof urlOrCallbacks === 'object') {
      return this.connectWithCallbacks(urlOrCallbacks);
    }

    const url = urlOrCallbacks || getWebSocketUrl();

    if (this.internalSocket?.connected || this.internalIsConnecting) {
      return;
    }

    this.internalIsConnecting = true;

    try {
      // Log configuration for debugging
      logWebSocketConfig();

      this.internalSocket = io(url, {
        transports: ['websocket', 'polling'],
        timeout: 20000,
        reconnection: false, // We'll handle reconnection manually
      });

      this.setupEventHandlers();
    } catch (error) {
      this.internalIsConnecting = false;
      throw error;
    }
  }

  /**
   * Connect with callbacks (used by useWebSocket hook)
   */
  private async connectWithCallbacks(callbacks: WebSocketEventCallbacks): Promise<boolean> {
    try {
      // Store callbacks
      this.internalCallbacks = { ...this.internalCallbacks, ...callbacks };

      // Connect to WebSocket
      this.connect();

      // Wait for connection to be established
      return new Promise((resolve) => {
        if (this.internalSocket?.connected) {
          resolve(true);
          return;
        }

        const checkConnection = () => {
          if (this.internalSocket?.connected) {
            resolve(true);
          } else if (!this.internalIsConnecting) {
            resolve(false);
          } else {
            setTimeout(checkConnection, 100);
          }
        };

        checkConnection();
      });
    } catch (error) {
      return false;
    }
  }

  /**
   * Set up event handlers
   */
  private setupEventHandlers(): void {
    if (!this.internalSocket) return;

    this.internalSocket.on('connect', () => {
      this.internalIsConnecting = false;
      this.internalReconnectAttempts = 0;
      this.internalReconnectDelay = 1000; // Reset delay

      // Re-register all callbacks
      Object.entries(this.internalCallbacks).forEach(([event, callback]) => {
        if (callback) {
          this.internalSocket?.on(event, callback);
        }
      });
    });

    this.internalSocket.on('disconnect', (reason) => {
      this.internalIsConnecting = false;

      // Only attempt reconnection for certain disconnect reasons
      if (reason === 'io server disconnect') {
        // Server initiated disconnect, don't reconnect automatically
        return;
      }

      this.handleReconnection();
    });

    this.internalSocket.on('connect_error', (error) => {
      this.internalIsConnecting = false;
      this.handleReconnection();
    });

    this.internalSocket.on('error', (error) => {
      // Error handling without console output
    });
  }

  /**
   * Handle reconnection logic
   */
  private handleReconnection(): void {
    if (this.internalReconnectAttempts >= this.internalMaxReconnectAttempts) {
      return;
    }

    this.internalReconnectAttempts++;

    setTimeout(() => {
      if (!this.internalSocket?.connected && !this.internalIsConnecting) {
        this.connect();
      }
    }, this.internalReconnectDelay);

    // Exponential backoff with jitter
    this.internalReconnectDelay = Math.min(
      this.internalReconnectDelay * 2 + Math.random() * 1000,
      30000 // Max 30 seconds
    );
  }

  /**
   * Authenticate user with WebSocket
   */
  authenticate(userId: string, username: string): void {
    if (!this.internalSocket?.connected) {
      return;
    }

    this.internalSocket.emit('authenticate', { userId, username });
  }

  /**
   * Subscribe to deal updates
   */
  subscribeToDeals(): void {
    if (!this.internalSocket?.connected) {
      return;
    }

    this.internalSocket.emit('subscribe_deals');
  }

  /**
   * Subscribe to comments for a specific deal
   */
  subscribeToComments(dealId: string): void {
    if (!this.internalSocket?.connected) {
      return;
    }

    this.internalSocket.emit('subscribe_comments', dealId);
  }

  /**
   * Subscribe to user notifications
   */
  subscribeToNotifications(userId: string): void {
    if (!this.internalSocket?.connected) {
      return;
    }

    this.internalSocket.emit('subscribe_notifications', userId);
  }

  /**
   * Disconnect WebSocket
   */
  disconnect(): void {
    if (this.internalSocket) {
      this.internalSocket.disconnect();
      this.internalSocket = null;
    }
    this.internalIsConnecting = false;
    this.internalReconnectAttempts = 0;
  }

  /**
   * Check connection status
   */
  isConnected(): boolean {
    return this.internalSocket?.connected || false;
  }

  /**
   * Get connection info
   */
  getConnectionInfo() {
    return {
      connected: this.isConnected(),
      socketId: this.internalSocket?.id,
      url: getWebSocketUrl(),
      reconnectAttempts: this.internalReconnectAttempts,
    };
  }

  emit(event: string, data: any): void {
    if (this.internalSocket?.connected) {
      this.internalSocket.emit(event, data);
    }
  }

  on(event: string, callback: (data: any) => void): void {
    this.internalCallbacks[event] = callback;

    if (this.internalSocket) {
      this.internalSocket.on(event, callback);
    }
  }

  off(event: string): void {
    delete this.internalCallbacks[event];

    if (this.internalSocket) {
      this.internalSocket.off(event);
    }
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
export default websocketService;
